import React from 'react';
import { t, Trans } from '@lingui/macro';
import styled from 'styled-components/native';

import Button from '@src/components/Button';
import Divider from '@src/components/Divider';
import Separator from '@src/components/Separator';
import { padding } from '@src/configuration/constants';
import useActionSheets from '@src/hooks/useActionSheets';
import useRegions from '@src/hooks/useRegions';
import { i18n } from '@src/i18n';

type Props = {
  onRegion?: (code?: string) => void;
  children?: React.ReactNode;
};

const Container = styled.View`
  background-color: ${({ theme }) => theme.colors.background};
`;

export const ItemsContainer = styled.View`
  padding: 0 ${padding}px;
`;

const Where = styled(Button).attrs(({ theme }) => ({
  mode: 'outlined',
  labelStyle: {
    color: theme.colors.onSurface,
  },
}))``;

const Component: React.FC<Props> = ({ onRegion, children }) => {
  const actionSheets = useActionSheets();
  const regions = useRegions();
  const [activeRegion, setActiveRegion] = React.useState<number>();
  const handlePress = React.useCallback(
    () =>
      actionSheets(
        {
          options: [i18n._(t`Delete filter`)].concat(
            regions.data.map((region) => region.name)
          ),
          title: i18n._(t`Where?`),
          cancelButtonIndex: activeRegion ?? 0,
          destructiveButtonIndex: 0,
        },
        (index) =>
          index && index !== 0
            ? setActiveRegion(index - 1)
            : setActiveRegion(undefined)
      ),
    [actionSheets, activeRegion, regions.data]
  );

  React.useEffect(() => {
    if (activeRegion !== undefined) {
      const code = regions.data?.[activeRegion]?.code;
      onRegion?.(code);
    } else {
      onRegion?.(undefined);
    }
  }, [activeRegion, onRegion, regions]);

  return (
    <>
      <Container>
        <Separator />
        <ItemsContainer>
          <Where onPress={handlePress}>
            {activeRegion === undefined ? (
              <Trans>Where?</Trans>
            ) : (
              regions.data?.[activeRegion]?.name
            )}
          </Where>
        </ItemsContainer>
        <Separator />
        {children}
        {children && <Separator />}
        <ItemsContainer>
          <Divider />
        </ItemsContainer>
      </Container>
      <Separator />
    </>
  );
};

Component.displayName = 'WhereButton';

export default React.memo(Component);
